import { ImageName } from "@mcleod/images";
import {
    BlurEvent, Button, ButtonVariant, ClickEvent, DataSourceMode, DropdownItem, Label, Layout, Panel, SlideoutDecorator, Snackbar,
    Toast
} from "@mcleod/components";
import {Api, HorizontalAlignment, Navigation, StringUtil} from "@mcleod/core";
import { DuplicateId, DuplicateIdProps } from "@mcleod/dispatch";
import { AutogenLayoutAddCarrier } from "./autogen/AutogenLayoutAddCarrier";
import {MultipleDocketNumberSelector} from "./MultipleDocketNumberSelector";
import {CommonDialogs} from "@mcleod/common";

const viewCarrierURL = "lme/dispatch/Carrier?mode=update&key=";
const DUPLICATE_VALIDATION_PROPS: DuplicateIdProps = {
    tableName: "payee",
    screenName: "Payee",
    navigateToUrl: viewCarrierURL
};

export class AddCarrier extends AutogenLayoutAddCarrier {
    private _vendorId: string;
    private _carrierName: string;
    private _carrierNumber: string;
    private _carrierIdentifier: string;
    private _carrierOnboardingServer: string;
    private _websiteUrl: string;
    private add_carrier_vendors = ['D', 'H', 'R'];
    private codeGenerationIsActive: boolean;
    private allowManualEntry: boolean;

    constructor(props?) {
        super(props);
        this._vendorId = props.vendorId;
        this._carrierName = props.carrierName;
        this._carrierNumber = props.carrierNumber;
        this._carrierIdentifier = props.carrierIdentifier;
        this._carrierOnboardingServer = props.carrierInsServer;
        this._websiteUrl = props.websiteUrl;
    }

    override async onLoad() {
        this.setRequiredFieldByVendor(this._vendorId);
        const newRow = await this.sourcePayee.createBlankRow();
        this.sourcePayee.setRowsAndMode(DataSourceMode.ADD, [newRow]);
        this.setAddCarrierScreen(this._carrierName, this._carrierNumber, this._carrierIdentifier, this._carrierOnboardingServer, this._websiteUrl);
        this.shouldShowCarrierIdField();
    }

    async shouldShowCarrierIdField() {
        Api.post("lme/general/code-generation-settings", { "table_name": "payee" }).then(response => {
            this.codeGenerationIsActive = response.data[0]?.is_active ?? false;
            this.allowManualEntry = response.data[0]?.allow_manual_entry ?? false;
            if (this.allowManualEntry || !this.codeGenerationIsActive) {
                this.textboxCarrierId.visible = true;
            } else {
                this.textboxCarrierId.visible = false;
            }

            if (!this.codeGenerationIsActive) {
                this.textboxCarrierId.required = true;
            } else {
                this.textboxCarrierId.required = false;
            }

            DUPLICATE_VALIDATION_PROPS.codeGenerationIsActive = this.codeGenerationIsActive;
            DuplicateId.configureIdTextbox(this.textboxCarrierId, DUPLICATE_VALIDATION_PROPS);
        });
    }

    private buttonAdd = new Button({
        rowBreak: false,
        backgroundColor: "primary",
        color: "primary.reverse",
        caption: "Add Carrier",
        borderWidth: 0,
        borderColor: "primary",
        onClick: (event: ClickEvent) => this.addCarrier(),
        enabled: false,
        disabledTooltip: "Please provide all fields to add a carrier."
    })

    static showSlideout(vendorId: string, carrierName: string, carrierType: string, carrierNumber: string, carrierInsServer: string, websiteUrl: string) {

        const layout = Layout.getLayout("lme/dispatch/AddCarrier", {
            vendorId: vendorId,
            carrierName: carrierName,
            carrierIdentifier: carrierType,
            carrierNumber: carrierNumber,
            carrierInsServer: carrierInsServer,
            websiteUrl: websiteUrl
        } as any) as AddCarrier;

        const slideoutDecorator = new SlideoutDecorator({
            title: getTitle(vendorId),
            layout: layout,
            fillVerticalSpace: true,
            width: 1000,
            overlayProps: {greyedBackground: true},
            addlComponents: layout.buttonAdd,
            onClose: (cancelled: boolean) => layout.slideOut()
        });
    }


    addCarrier() {
        this.addCarrierInfo(this.textboxAddCarrierName.text, this.textboxAddCarrierNbr.text, this.textboxAddCarrierType.text, this.textboxPacketContactName.text, this.textboxEmailAddress.text,null);
    }

    async addCarrierInfo(carrierName: string, identifierValue: string, identifierType: string, packetContactName: string, emailAddress: string, packetDotNumber: string) {
        this.buttonAdd.busy = true;

        if (!(await DuplicateId.validateBeforePost(this.textboxCarrierId, DUPLICATE_VALIDATION_PROPS))) {
            this.buttonAdd.busy = false;
            return false;
        }
        Api.post("lme/dispatch/add-carrier",
            {
                "carrier_name": carrierName,
                "carrier_type": identifierType,
                "carrier_identifier": identifierValue,
                "packet_contact_name": packetContactName,
                "email_address": emailAddress,
                "dot_number": packetDotNumber,
                "carrier_id": this.textboxCarrierId.getDataValue()
            })
            .then(response => {
                this.buttonAdd.busy = false;
                const responseData = response?.data?.[0];
                if (responseData?.carrier_found) {
                    Snackbar.showWarningSnackbar("Carrier: " + responseData?.carrier_id + " already exist in the system!");
                }else if(responseData?.add_carrier_response?.multiple_carriers_info){
                    const dotNumber = responseData?.add_carrier_response?.dotNumber;
                    const layout = Layout.getLayout("lme/powerbroker/MultipleDocketNumberSelector") as MultipleDocketNumberSelector;
                        layout.addLayoutLoadListener(()=>{
                            const dropdownItems = responseData?.add_carrier_response?.multiple_carriers_info?.map(
                                (carrier: { docketNumber: string }) => new DropdownItem(carrier.docketNumber)
                            );

                            layout.textboxDocketNumber.items = dropdownItems;
                            layout.textboxDotNumber.text = dotNumber;
                            const confirmDialog = CommonDialogs.createDialog(layout, {
                                title: `Select Docket Number`,
                                okVisible: true
                            });

                            confirmDialog.show().then(()=>Promise.resolve(confirmDialog).then(async result => {
                                if (!result.wasCancelled) {
                                    const selectedDocketNumber = layout.textboxDocketNumber.text;
                                    this.addCarrierInfo(carrierName, selectedDocketNumber, "MC Number", packetContactName, emailAddress,layout.textboxDotNumber.text);
                                }
                            }));
                        });
                } else {
                    if (StringUtil.isEmptyString(responseData?.add_carrier_error_message)) {
                        const { carrier_name, carrier_id, vendor_name } = responseData?.add_carrier_response || {};
                        const carrierAddedMessage = "Carrier, " + carrier_name + " (" + carrier_id + ") " + "has been added. ";
                        const carrierMonitoredMessage = "Carrier is now being monitored with " + vendor_name + ".";
                        this.slideOut().then(() => {
                            Toast.showToast(this.createSuccessToastPanel(carrierAddedMessage, carrierMonitoredMessage, carrier_id), {backgroundColor: "primary"}, {millisUntilDismissal: 10000});
                        });
                    } else {
                        Snackbar.showWarningSnackbar("There was an unexpected error adding the carrier.");
                    }
                }
            }).catch(error => {
            this.buttonAdd.busy = false;
            Snackbar.showWarningSnackbar("There was an unexpected error adding the carrier - " + error);
        });
    }


    createSuccessToastPanel(carrierAddedMessage: string, carrierMonitoredMessage: string, carrierId: string): Panel {
        const labelProps = {
            color: "white",
            fontSize: "xlarge",
            rowBreak: false,
            paddingLeft: 3,
            paddingRight: 1,
            marginLeft: 0,
            marginRight: 0,
            wrap: false
        };
        return new Panel({
            align: HorizontalAlignment.CENTER, fillRow: true,
            components: [
                new Label({...labelProps, text: carrierAddedMessage, rowBreak: true}),
                new Label({...labelProps, text: carrierMonitoredMessage, rowBreak: false}),
                new Button({
                    imageName: ImageName.detail,
                    imageWidth: 20,
                    imageHeight: 20,
                    margin: 0,
                    tooltip: "Click to go to Carrier screen",
                    color: "subtle.light",
                    variant: ButtonVariant.round,
                    onClick: () => this.viewCarrierLinkOnClick(carrierId),
                    rowBreak: false
                })
            ]
        });
    }

    viewCarrierLinkOnClick(carrierCode: string) {
        AddCarrier.navigateTo(carrierCode, true);
    }

    public static navigateTo(id: string, newTab = true): void {
        Navigation.navigateTo(viewCarrierURL + id, {newTab: newTab});
    }

    async setAddCarrierScreen(_carrierName: string, _carrierNumber: string, _carrierType: string, _carrierOnboardingServer: string, _websiteUrl: string) {
        await this.mainDataSource.search({
            "carrier_name": this._carrierName,
            "carrier_type": this._carrierIdentifier,
            "carrier_identifier": this._carrierNumber
        }).then(response => {
                if (response.modelRows[0].data.pending_carrier_type) {
                    const lookupItems: DropdownItem[] = this.textboxAddCarrierType.items as DropdownItem[]
                    this.textboxAddCarrierType.selectedItem = lookupItems.filter(item => item.value == response.modelRows[0].data.pending_carrier_type)[0]
                }
                if (response.modelRows[0].data.is_logix_licensed && StringUtil.isEmptyString(_carrierOnboardingServer) && StringUtil.isEmptyString(_websiteUrl)) {
                    this.textboxPacketContactName.visible = true;
                    this.textboxEmailAddress.visible = true;
                } else {
                    this.textboxPacketContactName.visible = false;
                    this.textboxEmailAddress.visible = false;
                }
                if (response.modelRows[0].data.carrier_found) {
                    Snackbar.showWarningSnackbar("Carrier: " + response.modelRows[0].data.carrier_id + " already exist in the system.");
                }
            });


            if (this._vendorId === 'D' || this._vendorId === 'H' || this._vendorId === 'R') {
                this.textboxAddCarrierName.visible = false;
                this.textboxPacketContactName.visible = false;
                this.textboxEmailAddress.visible = false;

                this.textboxAddCarrierType.items = [
                    new DropdownItem("MC Number", "MC Number"),
                    new DropdownItem("DOT Number", "DOT Number")
                ];

            }
        }

    textboxAddCarrierTypeOnBlur(event: BlurEvent) {
        this.enabledAddButton()
    }

    textboxAddCarrierNameOnBlur(event: BlurEvent) {
        this.enabledAddButton()
    }

    async textboxAddCarrierNbrOnBlur(event: BlurEvent) {
        this.enabledAddButton()

    }

    private setRequiredFieldByVendor(_vendorId: string) {
        this.textboxAddCarrierName.required = !this.add_carrier_vendors.includes(_vendorId);
    }

    private enabledAddButton() {
        const hasCarrierType = !StringUtil.isEmptyString(this.textboxAddCarrierType.text);
        const hasCarrierNumber = !StringUtil.isEmptyString(this.textboxAddCarrierNbr.text);
        const hasCarrierName = !this.textboxAddCarrierName.visible || !StringUtil.isEmptyString(this.textboxAddCarrierName.text);
        this.buttonAdd.enabled = hasCarrierType && hasCarrierNumber && hasCarrierName;
        this.buttonAdd.disabledTooltip = this.buttonAdd.enabled ? null : "Please provide all fields to add a carrier.";
    }
}

function getTitle(vendorId: string) {
    const vendorTitles = {
        S: "SaferWatch", C: "CarrierWatch", P: "TDS", R: "RMIS", H: "Highway", D: "MyCarrierPortal"
    };
    return `Adding Carrier with ${vendorTitles[vendorId] || "Logix Onboarding Solution"}`;
}
